import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import api from '../../services/api.js'
import { useAuth } from '../../hooks/useAuth.js'
import { useTranslation } from 'react-i18next'

function CheckItem({ label, passed, detail }) {
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-100 last:border-0">
      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5
        ${passed ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-500'}`}>
        {passed ? '✓' : '✗'}
      </div>
      <div>
        <p className={`text-sm font-medium ${passed ? 'text-gray-700' : 'text-gray-800'}`}>{label}</p>
        <p className={`text-xs mt-0.5 ${passed ? 'text-gray-400' : 'text-red-600'}`}>{detail}</p>
      </div>
    </div>
  )
}

const CHECK_LABELS = {
  all_deliverables_approved: 'All Deliverables Approved',
  active_contract: 'Active Contract Exists',
  invoices_raised: 'All Milestones Paid',
}

export default function ProjectClosure() {
  const { t } = useTranslation('projectsMisc')
  const { id: projectId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const isAdmin = user?.role === 'Admin'
  const isPM = user?.role === 'Project Manager'

  const [prereqs, setPrereqs] = useState(null)
  const [loading, setLoading] = useState(true)
  const [closureNotes, setClosureNotes] = useState('')

  // The closure templates on offer. Empty is a normal answer — nobody has to have any, and the
  // buttons simply do not appear.
  const [closureTemplates, setClosureTemplates] = useState([])
  useEffect(() => {
    api.get('/templates/?kind=closure')
      .then(r => setClosureTemplates(r.data?.items || []))
      .catch(() => setClosureTemplates([]))
  }, [])

  const useClosureTemplate = (tpl) => {
    // Headings with room under each, which is what somebody actually types into. Two newlines
    // rather than one, so the note does not read as a list of labels.
    const NL = String.fromCharCode(10)
    const body = (tpl.fields || [])
      .map(f => (f.label || f.key) + NL + NL)
      .join(NL)
    setClosureNotes(body)
  }
  // Legal hold. Read from the retention snapshot, which is the one place that knows both what
  // the periods are and whether anything may act on them.
  const [hold, setHold] = useState(null)
  const [holdBusy, setHoldBusy] = useState(false)
  const loadHold = async () => {
    try {
      const r = await api.get(`/closure/retention/${projectId}`)
      setHold({ on: !!r.data?.legal_hold, reason: r.data?.legal_hold_reason || '' })
    } catch { setHold(null) }
  }
  useEffect(() => { loadHold() }, [projectId])

  const toggleHold = async (on) => {
    let reason = hold?.reason || ''
    if (on) {
      reason = window.prompt(
        'Why is this project being held?' + String.fromCharCode(10) + String.fromCharCode(10)
        + 'A dispute, an audit, a court order. Whoever finds this in a year needs to know what '
        + 'it was for.', reason) || ''
      if (!reason.trim()) return
    } else if (!window.confirm(
        'Lift the hold? Retention periods apply again, and expired files become eligible for '
        + 'removal.')) {
      return
    }
    setHoldBusy(true); setMessage(null)
    try {
      const r = await api.post(`/closure/${projectId}/legal-hold`, { on, reason })
      setMessage({ type: 'success', text: r.data?.detail })
      await loadHold()
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Could not change the hold.' })
    } finally { setHoldBusy(false) }
  }

  const [actionLoading, setActionLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [torFile, setTorFile] = useState(null)
  const [torUploading, setTorUploading] = useState(false)
  const [torUploaded, setTorUploaded] = useState(false)

  const loadPrereqs = () => {
    setLoading(true)
    api.get(`/closure/prerequisites/${projectId}`)
      .then(r => { setPrereqs(r.data); setTorUploaded(!!r.data?.has_tor_document) })
      .catch(e => setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to load prerequisites' }))
      .finally(() => setLoading(false))
  }

  useEffect(() => { loadPrereqs() }, [projectId])

  const initiateClosure = async () => {
    setActionLoading(true)
    setMessage(null)
    try {
      const resp = await api.post(`/closure/initiate/${projectId}`, { closure_notes: closureNotes })
      setMessage({ type: 'success', text: resp.data.detail })
      loadPrereqs()
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to initiate closure' })
    } finally {
      setActionLoading(false)
    }
  }

  const confirmClosure = async () => {
    setActionLoading(true)
    setMessage(null)
    try {
      const resp = await api.post(`/closure/confirm/${projectId}`, {})
      setMessage({ type: 'success', text: resp.data.detail })
      loadPrereqs()
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to confirm closure' })
    } finally {
      setActionLoading(false)
    }
  }

  const uploadTor = async () => {
    if (!torFile) return
    setTorUploading(true)
    setMessage(null)
    try {
      const fd = new FormData()
      fd.append('file', torFile)
      await api.post(`/closure/${projectId}/tor-document`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setTorUploaded(true)
      setMessage({ type: 'success', text: 'TOR document uploaded. You can now archive the project.' })
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to upload TOR document' })
    } finally {
      setTorUploading(false)
    }
  }

  const [exporting, setExporting] = useState(false)
  const [exportNote, setExportNote] = useState(null)

  const downloadExport = async () => {
    setExporting(true); setExportNote(null)
    try {
      setExportNote(exportMessage(await downloadProjectExport(projectId)))
    } catch (e) {
      setExportNote({ bad: true, text: e.message })
    } finally { setExporting(false) }
  }

  const archiveProject = async () => {
    setActionLoading(true)
    setMessage(null)
    try {
      const resp = await api.post(`/closure/archive/${projectId}`, {})
      setMessage({ type: 'success', text: resp.data.detail })
      loadPrereqs()
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || 'Failed to archive project' })
    } finally {
      setActionLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  const status = prereqs?.project_status
  const readyToClose = prereqs?.ready_to_close

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <button
        onClick={() => navigate(`/projects/${projectId}`)}
        className="text-xs text-gray-400 hover:text-gray-600 mb-4 flex items-center gap-1"
      >
        ← Back to Project
      </button>

      <h1 className="text-2xl font-bold text-gray-800 mb-1">{t('project_closure')}</h1>
      <p className="text-sm text-gray-500 mb-6">
        Review prerequisites before initiating the official closure process.
      </p>

      {message && (
        <div className={`mb-5 px-4 py-3 rounded-lg text-sm border ${
          message.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-red-50 border-red-200 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      {/* Status badge */}
      <div className="mb-5">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('current_status')}</span>
        <div className="mt-1 inline-block px-3 py-1 rounded-full text-sm font-semibold bg-blue-50 text-blue-700 border border-blue-200">
          {status}
        </div>
      </div>

      {/* Prerequisites — these gate INITIATING closure, so only relevant pre-closure.
          Once Closed, closing itself expires the contract, so "Active Contract Exists"
          would always read as failed here even though nothing is wrong. */}
      {prereqs?.checks && status === 'In Progress' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
          <h3 className="font-semibold text-gray-700 mb-3">{t('closure_prerequisites')}</h3>
          {Object.entries(prereqs.checks).map(([key, val]) => (
            <CheckItem
              key={key}
              label={CHECK_LABELS[key] || key}
              passed={val.passed}
              detail={val.detail}
            />
          ))}
        </div>
      )}

      {/* Actions */}
      {(isAdmin || isPM) && status === 'In Progress' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
          <h3 className="font-semibold text-gray-700 mb-3">{t('initiate_closure')}</h3>
          {!readyToClose && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
              Some prerequisites are not yet met. Resolve them before closing the project.
            </p>
          )}
          {/* A CLOSURE NOTE WITH A SHAPE, IF ONE IS WANTED.
              The box below is free text, so what goes in it has depended on who was typing and what
              they happened to remember. A template drops the headings in; the words are still theirs.
              Offered rather than imposed, and only while the box is empty, so nothing typed is lost. */}
          {closureTemplates.length > 0 && !closureNotes.trim() && (
            <div className="mb-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500">Start from:</span>
              {closureTemplates.map(tpl => (
                <button key={tpl.id} type="button" onClick={() => useClosureTemplate(tpl)}
                        title={tpl.description || ''}
                        className="text-xs px-2 py-1 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50">
                  {tpl.name}
                </button>
              ))}
            </div>
          )}
          <label className="block text-xs font-medium text-gray-600 mb-1">{t('closure_notes')}</label>
          <textarea
            value={closureNotes}
            onChange={e => setClosureNotes(e.target.value)}
            rows={closureNotes.split(String.fromCharCode(10)).length > 2 ? 12 : 3}
            placeholder="Add any final notes or handover instructions..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3"
          />
          <button
            onClick={initiateClosure}
            disabled={actionLoading || !readyToClose}
            className="px-5 py-2 text-sm bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 disabled:opacity-50"
          >
            {actionLoading ? 'Processing...' : 'Initiate Closure Request'}
          </button>
        </div>
      )}

      {isAdmin && status === 'Pending Closure' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-5">
          <h3 className="font-semibold text-gray-700 mb-2">{t('confirm_closure')}</h3>
          <p className="text-sm text-gray-500 mb-4">
            A PM has requested closure. Confirm to officially close this project.
          </p>
          <button
            onClick={confirmClosure}
            disabled={actionLoading}
            className="px-5 py-2 text-sm bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
          >
            {actionLoading ? 'Processing...' : 'Confirm & Close Project'}
          </button>
        </div>
      )}

      {isAdmin && status === 'Closed' && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-700 mb-2">{t('archive_project')}</h3>
          <p className="text-sm text-gray-500 mb-4">
            Archiving makes the project read-only for long-term retention.
          </p>

          {!torUploaded && (
            <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <label className="block text-xs font-medium text-gray-600 mb-3">
                TOR (Terms of Reference) document — required before archiving
              </label>
              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-gray-400 shrink-0">1.</span>
                <label className="px-4 py-1.5 text-sm bg-white border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-100 cursor-pointer shrink-0">
                  Choose File
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={e => setTorFile(e.target.files?.[0] || null)}
                    className="hidden"
                  />
                </label>
                <span className="text-xs text-gray-500 truncate flex-1">
                  {torFile ? torFile.name : 'No file chosen'}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-xs font-semibold text-gray-400 shrink-0">2.</span>
                <button
                  onClick={uploadTor}
                  disabled={!torFile || torUploading}
                  className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 shrink-0"
                >
                  {torUploading ? 'Uploading...' : 'Upload TOR'}
                </button>
                {!torFile && <span className="text-xs text-gray-400">{t('choose_file_first')}</span>}
              </div>
            </div>
          )}

          <button
            onClick={archiveProject}
            disabled={actionLoading || !torUploaded}
            title={!torUploaded ? 'Upload the TOR document above before archiving' : undefined}
            className="px-5 py-2 text-sm bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50"
          >
            {actionLoading ? 'Processing...' : 'Archive Project'}
          </button>
        </div>
      )}

      {/* A HOLD BEATS EVERY RETENTION PERIOD.
          Placed here rather than on the archived banner, because a dispute starts long before a
          project is archived and the hold has to survive into it. Admin only: a hold is asserted
          because somebody outside this system said so, and lifting one removes the only thing
          standing between a retention date and files being deleted. */}
      {isAdmin && hold && (
        <div className={`rounded-xl border p-5 mt-4 ${hold.on
          ? 'bg-amber-50 border-amber-300' : 'bg-white border-gray-200'}`}>
          <h3 className="font-semibold text-gray-700 mb-2">Legal hold</h3>
          {hold.on ? (
            <>
              <p className="text-sm text-amber-900 mb-1">
                <strong>This project is under legal hold.</strong> Nothing about it can be purged,
                whatever its retention periods say.
              </p>
              {hold.reason && (
                <p className="text-sm text-amber-800 mb-4">Reason given: {hold.reason}</p>
              )}
              <button onClick={() => toggleHold(false)} disabled={holdBusy}
                      className="px-5 py-2 text-sm bg-white text-red-700 border border-red-300 rounded-lg font-medium hover:bg-red-50 disabled:opacity-50">
                {holdBusy ? 'Working…' : 'Lift the hold'}
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-4">
                For a dispute, an audit or a court order. While a hold is on, no file for this
                project can be removed even after its retention period ends.
              </p>
              <button onClick={() => toggleHold(true)} disabled={holdBusy}
                      className="px-5 py-2 text-sm bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50">
                {holdBusy ? 'Working…' : 'Place a legal hold'}
              </button>
            </>
          )}
        </div>
      )}

      {/* HANDING THE RECORDS BACK.
          The reason this exists: the records outlive the work. A finished project can be kept
          for ten years and, until now, never handed to the person who paid for it. Reads only —
          exporting changes nothing and can be done as often as anybody asks, archived or not. */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mt-4">
        <h3 className="font-semibold text-gray-700 mb-2">Export everything</h3>
        <p className="text-sm text-gray-500 mb-4">
          One file holding every record and document for this project — readable by somebody who
          does not have ProTrack. Nothing is changed or removed.
        </p>
        {exportNote && (
          <p className={`text-sm mb-3 px-3 py-2 rounded-lg border ${exportNote.bad
            ? 'text-amber-800 bg-amber-50 border-amber-200'
            : 'text-green-700 bg-green-50 border-green-200'}`}>
            {exportNote.text}
          </p>
        )}
        <button
          onClick={downloadExport}
          disabled={exporting}
          className="px-5 py-2 text-sm bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50"
        >
          {exporting ? 'Building the file…' : 'Download export'}
        </button>
      </div>
    </div>
  )
}
